import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { usePromoterAuth } from '../../contexts/PromoterAuthContext';
import Layout from '../../components/shared/Layout/Layout';
import Button from '../../components/shared/Button/Button';
import {
  AuthContainer,
  AuthCard,
  AuthTitle,
  AuthSubtitle,
  AuthFooter,
  FormGroup,
  FormLabel,
  FormInput,
  FormHint,
  ErrorMessage,
} from '../../components/shared/Form/Form.styles';

const PASSWORD_MIN = 8;
const PASSWORD_MAX = 128;

const PromoterRegister = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    city: '',
    password: '',
    confirmPassword: '',
  });
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const { register, isAuthenticated, loading } = usePromoterAuth();
  const navigate = useNavigate();

  // Si ya hay una sesión de promotor, directo a su panel.
  useEffect(() => {
    if (!loading && isAuthenticated && !submitting) navigate('/promotor', { replace: true });
  }, [loading, isAuthenticated, submitting, navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (formData.password !== formData.confirmPassword) {
      setError('Las contraseñas no coinciden.');
      return;
    }
    if (formData.password.length < PASSWORD_MIN) {
      setError(`La contraseña debe tener al menos ${PASSWORD_MIN} caracteres.`);
      return;
    }

    setSubmitting(true);
    const payload = {
      name: formData.name.trim(),
      email: formData.email.trim(),
      password: formData.password,
    };
    if (formData.phone.trim()) payload.phone = formData.phone.trim();
    if (formData.city.trim()) payload.city = formData.city.trim();

    const result = await register(payload);
    if (result.success) {
      // Queda pendiente de aprobación: el panel muestra ese estado.
      navigate('/promotor', { replace: true });
    } else {
      setError(result.error || 'No se pudo crear la cuenta.');
      setSubmitting(false);
    }
  };

  return (
    <Layout headerType="promoter">
      <AuthContainer>
        <AuthCard>
          <AuthTitle>Quiero ser promotor</AuthTitle>
          <AuthSubtitle>
            Comparte actividades de empresas aliadas con tu enlace y gana una comisión por cada venta.
            Tu cuenta debe ser aprobada por BokerHub antes de operar.
          </AuthSubtitle>

          {error && <ErrorMessage role="alert">{error}</ErrorMessage>}

          <form onSubmit={handleSubmit}>
            <FormGroup>
              <FormLabel htmlFor="pr-name">Nombre completo</FormLabel>
              <FormInput
                id="pr-name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                maxLength={100}
                autoComplete="name"
                required
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="pr-email">Email</FormLabel>
              <FormInput
                id="pr-email"
                type="email"
                name="email"
                value={formData.email}
                onChange={handleChange}
                placeholder="tu@email.com"
                autoComplete="email"
                required
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="pr-phone">Teléfono (opcional)</FormLabel>
              <FormInput
                id="pr-phone"
                type="tel"
                name="phone"
                value={formData.phone}
                onChange={handleChange}
                maxLength={20}
                autoComplete="tel"
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="pr-city">Ciudad (opcional)</FormLabel>
              <FormInput
                id="pr-city"
                name="city"
                value={formData.city}
                onChange={handleChange}
                maxLength={100}
                autoComplete="address-level2"
              />
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="pr-password">Contraseña</FormLabel>
              <FormInput
                id="pr-password"
                type="password"
                name="password"
                value={formData.password}
                onChange={handleChange}
                minLength={PASSWORD_MIN}
                maxLength={PASSWORD_MAX}
                autoComplete="new-password"
                required
              />
              <FormHint>Entre {PASSWORD_MIN} y {PASSWORD_MAX} caracteres. No hay cambio de contraseña por ahora: guárdala bien.</FormHint>
            </FormGroup>
            <FormGroup>
              <FormLabel htmlFor="pr-confirm">Confirmar contraseña</FormLabel>
              <FormInput
                id="pr-confirm"
                type="password"
                name="confirmPassword"
                value={formData.confirmPassword}
                onChange={handleChange}
                maxLength={PASSWORD_MAX}
                autoComplete="new-password"
                required
              />
            </FormGroup>

            <Button type="submit" fullWidth disabled={submitting}>
              {submitting ? 'Creando cuenta...' : 'Crear cuenta de promotor'}
            </Button>
          </form>

          <AuthFooter>
            <p>¿Ya tienes cuenta de promotor? <Link to="/promotor/login">Inicia sesión</Link></p>
            <p>¿Tienes una empresa? Solicita tu perfil de promotor desde <Link to="/empresa/promotor">el portal de empresa</Link>.</p>
          </AuthFooter>
        </AuthCard>
      </AuthContainer>
    </Layout>
  );
};

export default PromoterRegister;
